import React, { useState } from "react";
import { 
  useRegisterUserMutation, 
  useVerifyCodeMutation,
  useRegisterVerificationMutation 
} from "../../rtkq/services/userAuthApi";

export const SignupForm = ({ 
  isVerification, 
  setIsVerification, 
  regEmail, 
  setRegEmail, 
  setIsLogin,
  resetForm
}) => {
  // Registration states
  const [regFirstName, setRegFirstName] = useState("");
  const [regLastName, setRegLastName] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regError, setRegError] = useState("");
  
  // Verification states
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");

  const [registerUser] = useRegisterUserMutation();
  const [verifyCode, { isLoading: isVerifyCodeLoading }] = useVerifyCodeMutation();
  const [registerVerification, { isLoading: isRegisterLoading }] = useRegisterVerificationMutation();

  const togglePasswordVisibility = () => {
    setShowRegPassword(!showRegPassword);
  };

  const resetFormErrors = () => {
    setRegError("");
    setVerificationError("");
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setRegError("");
    
    // Password validation
    if (regPassword !== confirmPassword) {
      setRegError("Passwords do not match");
      return;
    }
    
    if (regPassword.length < 8) {
      setRegError("Password must be at least 8 characters long");
      return;
    }
    
    try {
      const res = await registerVerification({
        first_name: regFirstName,
        last_name: regLastName, 
        email: regEmail, 
        password: regPassword,
        role: "student" // Default role, adjust as needed
      });
      
      if (res.error) {
        console.log("Registration error:", res.error);
        
        // Check for email-specific errors first
        if (res.error.data.errors?.email) {
          // Handle array of errors or single string
          if (Array.isArray(res.error.data.errors.email)) {
            setRegError(res.error.data.errors.email[0]);
          } else {
            setRegError(res.error.data.errors.email);
          }
        } else if (res.error.data?.non_field_errors) {
          // Handle non-field errors
          setRegError(res.error.data.non_field_errors[0]);
        } else {
          // Fallback for any other error format
          setRegError("Registration failed. Please try again.");
        }
        return;
      }
      
      if (res.data) {
        console.log("Registration email sent:", res.data);
        // Proceed to verification step
        setIsVerification(true);
      }
      
    } catch (error) {
      console.error("Registration error:", error);
      setRegError("Failed to register. Please try again.");
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerificationError("");
    
    try {
      const res = await verifyCode({ 
        email: regEmail, 
        verification_code: verificationCode 
      });
      
      if (res.error) {
        console.log("Verification error:", res.error);
        setVerificationError(res.error.data?.non_field_errors?.[0] || "Invalid verification code");
        return;
      }
      
      if (res.data) {
        console.log("Verification successful:", res.data);
        
        // Show success message
        alert("Account created successfully! Please log in.");
        
        // Reset to login form
        setIsLogin(true);
        setIsVerification(false);
        resetForm();
      }
      
    } catch (error) {
      console.error("Verification error:", error);
      setVerificationError("Invalid verification code. Please try again.");
    }
  };

  // Render verification form when in verification mode
  if (isVerification) {
    return (
      <form className="space-y-6" onSubmit={handleVerifyCode}>
        <div className="text-center mb-4">
          <h3 className="text-lg font-medium text-gray-700">Email Verification</h3>
          <p className="text-sm text-gray-500 mt-1">
            We've sent a verification code to {regEmail}. 
            Please enter the code below to complete your registration.
            The code can also be in your spam folder.
          </p>
        </div>
        
        <div>
          <label 
            htmlFor="verification_code" 
            className="block text-sm font-medium text-gray-700"
          >
            Verification Code
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="verification_code"
              id="verification_code"
              required
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter verification code"
            />
          </div>
        </div>

        {verificationError && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-md">
            {verificationError}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isVerifyCodeLoading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                      ${isVerifyCodeLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} 
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300`}
          >
            {isVerifyCodeLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </div>
            ) : (
              'Verify Email'
            )}
          </button>
        </div>
        
        <div className="text-center text-sm">
          <span className="text-gray-500">Didn't receive the code? </span>
          <button 
            type="button" 
            onClick={handleSignup} 
            className="text-green-600 hover:text-green-500 font-medium"
          >
            Resend
          </button>
        </div>
      </form>
    );
  }

  // Otherwise render signup form
  return (
    <form className="space-y-4" onSubmit={handleSignup}>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label 
            htmlFor="first_name" 
            className="block text-sm font-medium text-gray-700"
          >
            First Name
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="first_name"
              id="first_name"
              required
              value={regFirstName}
              onChange={(e) => setRegFirstName(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="First Name"
            />
          </div>
        </div>
        <div>
          <label 
            htmlFor="last_name" 
            className="block text-sm font-medium text-gray-700"
          >
            Last Name
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="last_name"
              id="last_name"
              required
              value={regLastName}
              onChange={(e) => setRegLastName(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Last Name"
            />
          </div>
        </div>
      </div>

      <div>
        <label 
          htmlFor="reg_email" 
          className="block text-sm font-medium text-gray-700"
        >
          Email
        </label>
        <div className="mt-1">
          <input
            type="email"
            name="reg_email"
            id="reg_email"
            required
            value={regEmail}
            onChange={(e) => setRegEmail(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Enter your email"
          />
        </div>
      </div>

      <div>
        <label 
          htmlFor="reg_password" 
          className="block text-sm font-medium text-gray-700"
        >
          Password
        </label>
        <div className="mt-1">
          <input
            type={showRegPassword ? "text" : "password"}
            name="reg_password"
            id="reg_password"
            required
            value={regPassword}
            onChange={(e) => setRegPassword(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Enter password (min. 8 characters)"
          />
        </div>
      </div>

      <div>
        <label 
          htmlFor="confirm_password" 
          className="block text-sm font-medium text-gray-700"
        >
          Confirm Password
        </label>
        <div className="mt-1">
          <input
            type={showRegPassword ? "text" : "password"}
            name="confirm_password"
            id="confirm_password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Confirm your password"
          />
        </div>
      </div>

      {regError && (
        <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-md">
          {regError}
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id="showRegPassword"
          checked={showRegPassword}
          onChange={togglePasswordVisibility}
          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
        />
        <label 
          htmlFor="showRegPassword" 
          className="ml-2 block text-sm text-gray-700 cursor-pointer"
        >
          Show Password
        </label>
      </div>

      <div>
        <button
          type="submit"
          disabled={isRegisterLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                    ${isRegisterLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300`}
        >
          {isRegisterLoading ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </div>
          ) : (
            'Sign Up'
          )}
        </button>
      </div>
    </form>
  );
};
